import * as yup from 'yup'
import { Controller, useForm } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import { checkEmailRegex, validationMessages } from 'constants/validation'

import { InviteResult, usePostInvite } from 'interface/user/invite'

import { FormHelperText, Stack, TextField } from '@mui/material'
import { Section } from 'components/controls/section'
import { GroupResult } from 'interface/user/group'
import { OptionSelect } from 'components/controls/optionSelect'

const {
  Required,
  InvalidEMailAddress,
  TooLongLength
} = validationMessages

const formSchema = yup.object().shape({
  userName: yup
    .string()
    .nullable()
		.max(20, TooLongLength(20)),
  email: yup
    .string()
    .required(Required)
		.max(512, TooLongLength(512))
    .matches(checkEmailRegex, InvalidEMailAddress),
  groups: yup.array()
    .min(1, Required)
    .required(Required),
})

type useInviteFormProps = {
  groupList: GroupResult[],
  invite?: InviteResult,
  onSubmit: (result: InviteResult) => void
}
export const useInviteForm = ({
  groupList,
  invite,
  onSubmit
}: useInviteFormProps) => {
  const form = useForm({
    defaultValues: invite ? {
      userName: invite.userName,
      email: invite.email,
      groups: invite.groups.map(group => group.groupId)
    } : {
      userName: null,
      email: '',
      groups: []
    },
    mode: 'onChange',
    criteriaMode: 'all',
		resolver: yupResolver(formSchema)
  })
  const [postState, addInvite] = usePostInvite({ onPosted: onSubmit })
  return {
    ...form,
    groupList,
    postState,
    submit: form.handleSubmit(input => addInvite({
      userName: input.userName ?? null,
      email: input.email,
      groups: input.groups
    }))
  }
}

export const InviteForm = ({
  groupList,
  control,
  formState: { errors }
}: ReturnType<typeof useInviteForm>) => (
  <Stack direction="column">
    <Section label="ユーザー名" labelProps={{ paddingY: 2 }}>
      <Controller
        control={control}
        name="userName"
        render={({ field }) => (
          <TextField
            {...field}
            variant="outlined"
            placeholder="ユーザー名"
            size="small"
            error={!!errors.userName}
            helperText={errors.userName ? errors.userName.message : ''}
          />
        )}
      />
      <FormHelperText>
        ※省略可能
      </FormHelperText>
    </Section>

    <Section label="メールアドレス" labelProps={{ py: 2 }}>
      <Controller
        control={control}
        name="email"
        render={({ field }) => (
          <TextField
            placeholder="メールアドレス"
            {...field}
            variant="outlined"
            size="small"
            error={!!errors.email}
            helperText={errors.email ? errors.email.message : ''}
          />
        )}
      />
    </Section>

    <Section label="所属グループ" labelProps={{ py: 2 }}>
      <Controller
        control={control}
        name="groups"
        render={({field}) => (
          <>
            <OptionSelect
              {...field}
              variant="outlined"
              size="small"
              optionList={(groupList ?? []).map((group) => ({
                id: group.groupId,
                label: group.groupName
              }))}
            />
            {errors.groups && (
              <FormHelperText error>{errors.groups.message}</FormHelperText>
            )}
          </>
        )}
      />  
    </Section>
  </Stack>
)
